import { useState, useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  copilotApi,
  ChatMessage,
  ChatResponse,
  MessageHistoryDto,
} from "../../features/copilot/copilot.api";
import { useUserAppState } from "../../hooks/useUserAppState";
import { trackEvent } from "../../features/analytics/analytics";
import { markConversationsDirtyAtom } from "../../features/copilot/copilot.atoms";
import { suppressConciergeHandoffAtom } from "./copilotAtoms";

// Get timezone offset in ISO format (e.g., "-05:00" for EST)
function getTimezoneOffset(): string {
  const offset = new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset <= 0 ? "+" : "-";
  return `${sign}${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}

interface Interaction {
  userMessage: string;
  response: ChatResponse;
  timestamp: Date;
}

interface UseCopilotChatOptions {
  mode?: string;
  customPrompt?: string; // Complete system prompt override for Lab testing
  testAccountId?: number; // Lab: Test with different account context
  bypassRateLimits?: boolean; // Lab: Bypass rate limits for testing
}

export function useCopilotChat(options?: UseCopilotChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<ChatResponse | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);

  // Get current user app state for semantic actions
  const userAppState = useUserAppState();

  // Mark conversations as needing refresh (only when real message sent, not test mode)
  const markConversationsDirty = useSetAtom(markConversationsDirtyAtom);

  // One-shot flag set by ExhaustionWall to suppress Concierge handoff for the
  // next user message (plan-help prefill shouldn't be re-answered by Concierge).
  const [suppressConciergeHandoff, setSuppressConciergeHandoff] = useAtom(
    suppressConciergeHandoffAtom,
  );

  const sendMessage = useCallback(
    async (message: string) => {
      setLoading(true);
      setError(null);

      // Track conversation start (first message in new conversation)
      const isNewConversation = messages.length === 0;

      trackEvent({
        category: "ai-copilot",
        action: isNewConversation ? "conversation" : "message",
        label: (messages.length + 1).toString(),
        feature: true,
      });

      // Add user message immediately
      const userMessage: ChatMessage = {
        role: "user",
        content: message,
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        // Build history for test mode (frontend-cached messages)
        let history: MessageHistoryDto[] | undefined;
        if (options?.testAccountId && messages.length > 0) {
          history = messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          }));
        }

        const response = await copilotApi.sendMessage({
          message,
          conversationId,
          sessionId: generateSessionId(),
          mode: options?.mode,
          customPrompt: options?.customPrompt,
          testAccountId: options?.testAccountId,
          history,
          bypassRateLimits: options?.bypassRateLimits,
          userAppState, // Current user app state for semantic actions
          // Pass browser timezone for proper time display
          timezoneOffset: getTimezoneOffset(),
          timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        // Add assistant response
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.response,
          createdAt: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Capture + reset the one-shot suppression flag early so we don't leak
        // it into a later send if this send fails before the handoff write below.
        const skipHandoffStorage = suppressConciergeHandoff;
        if (suppressConciergeHandoff) setSuppressConciergeHandoff(false);

        // Update conversationId only if response has one (null in test mode)
        if (response.conversationId !== null) {
          setConversationId(response.conversationId);

          // Persist for Concierge handoff — when user clicks action:concierge,
          // ConciergePage reads this to inject prior Copilot context into the session.
          // Always keep conversation_id so handoff has context; skip last-user-message
          // when the message originated from ExhaustionWall (plan-help prefill shouldn't
          // be auto-re-sent by Concierge, which would waste credits on a wrong-domain answer).
          sessionStorage.setItem('copilot_conversation_id', response.conversationId.toString());
          if (!skipHandoffStorage) {
            sessionStorage.setItem('copilot_last_user_message', message);
          }

          // Mark conversations as dirty - will refresh when History tab opened
          markConversationsDirty();
        }
        setLastResponse(response);

        // Track interaction for debug log
        setInteractions((prev) => [
          ...prev,
          {
            userMessage: message,
            response,
            timestamp: new Date(),
          },
        ]);

        return true; // Success
      } catch (err) {
        // Extract user-friendly error message from API response
        let errorMessage = "Failed to send message";

        // Check if it's an Axios error with response data
        if (err && typeof err === "object" && "response" in err) {
          const axiosError = err as {
            response?: { status?: number; data?: { error?: string } };
          };

          if (axiosError.response?.status === 429) {
            // Rate limit error - use the error message from API
            errorMessage =
              axiosError.response?.data?.error ||
              "You've reached your rate limit. Please try again later.";
          } else if (axiosError.response?.status === 503) {
            // Service unavailable (AI overloaded) - use the error message from API
            errorMessage =
              axiosError.response?.data?.error ||
              "Our AI assistant is temporarily unavailable. Please try again in a moment.";
          } else if (axiosError.response?.data?.error) {
            errorMessage = axiosError.response.data.error;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        // Remove optimistic user message on error
        setMessages((prev) => prev.slice(0, -1));
        return false; // Failure
      } finally {
        setLoading(false);
      }
    },
    [
      messages,
      options?.testAccountId,
      options?.mode,
      options?.customPrompt,
      options?.bypassRateLimits,
      conversationId,
      userAppState,
      markConversationsDirty,
    ]
  );

  const sendFeedback = useCallback(
    async (rating: number, comment?: string) => {
      if (!conversationId) return;

      try {
        await copilotApi.saveFeedback({
          conversationId,
          rating,
          comment,
        });
      } catch (err) {
        console.error("Failed to save feedback:", err);
      }
    },
    [conversationId]
  );

  const reset = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
    setError(null);
    setLastResponse(null);
    setInteractions([]);
  }, []);

  const loadConversation = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const messages = await copilotApi.getConversation(id);
      setMessages(messages);
      setConversationId(id);
      setInteractions([]); // Clear interactions when loading old conversation
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load conversation"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    messages,
    loading,
    error,
    conversationId,
    lastResponse,
    interactions,
    sendMessage,
    sendFeedback,
    reset,
    loadConversation,
  };
}

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}
